import React, { useState } from 'react';
import moment from 'moment';
import makeStyles from '@mui/styles/makeStyles';
import { Done, DoneAll, ErrorOutline } from '@mui/icons-material';
import { grey, green, red } from '@mui/material/colors';
import { Avatar, Box, Stack, Typography } from '@mui/material';
import { SmsDirection, SmsActivityStatus } from '@trnsact/trnsact-shared-types';
import { DealSmsActivity } from '../../api/types';
import { DateFormats } from '../../../../constants/common';
import { generatePersonaFullName, getPersonaInitials } from 'utils/formatters';
import { useSelector } from 'react-redux';

interface Props {
  message: DealSmsActivity;
}

export const ConversationMessageBubble = ({ message }: Props) => {
  const currentUserProfile = useSelector((state: any) => state.userProfile);

  const isOutgoing = message.smsDirection === SmsDirection.Outgoing;
  const isFailed = message.status === SmsActivityStatus.Failed;

  const contactName = generatePersonaFullName(message.ownerPgCreditApplication);
  const recepientInitials = getPersonaInitials(
    message.ownerPgCreditApplication?.firstName,
    message.ownerPgCreditApplication?.lastName
  );

  const senderName = generatePersonaFullName(message.createdByUserProfile);
  const senderInitials = getPersonaInitials(
    message.createdByUserProfile?.firstName,
    message.createdByUserProfile?.lastName
  );

  const classes = useStyles({ isOutgoing });

  return (
    <Stack direction="row" justifyContent={isOutgoing ? 'flex-end' : 'flex-start'} alignItems="flex-end" gap="0.5rem">
      {!isOutgoing && (
        <Avatar className={classes.avatar}>
          <Typography fontSize="0.75rem" fontWeight={500}>
            {recepientInitials}
          </Typography>
        </Avatar>
      )}

      <Stack maxWidth="70%" gap="0.25rem">
        {!isOutgoing && (
          <Typography variant="caption" color="textSecondary" fontWeight={500}>
            {contactName}
          </Typography>
        )}

        <Box className={classes.bubble}>
          <Stack gap="0.25rem">
            {isOutgoing && (
              <Typography variant="caption" fontWeight={500} color="white">
                {currentUserProfile.id === message.createdByUserProfileId ? 'You' : senderName}
              </Typography>
            )}

            <Typography variant="body2">{message.message}</Typography>

            <Stack direction="row" justifyContent="flex-end" alignItems="center" gap="0.25rem">
              <Typography variant="caption" color={isOutgoing ? 'rgba(255,255,255,0.7)' : 'textSecondary'}>
                {moment(message.createdDateTime).format(DateFormats.DateTimeMedium12Hour)}
              </Typography>

              {isOutgoing && message.status === SmsActivityStatus.Sent && (
                <Done sx={{ fontSize: 14, color: 'rgba(255,255,255,0.7)' }} />
              )}
              {isOutgoing && message.status === SmsActivityStatus.Delivered && (
                <DoneAll sx={{ fontSize: 14, color: 'rgba(255,255,255,0.7)' }} />
              )}
              {isFailed && <ErrorOutline sx={{ fontSize: 14, color: red[400] }} />}
            </Stack>
          </Stack>
        </Box>
      </Stack>

      {isOutgoing && (
        <Avatar className={classes.avatar}>
          <Typography fontSize="0.75rem" fontWeight={500}>
            {senderInitials}
          </Typography>
        </Avatar>
      )}
    </Stack>
  );
};

const useStyles = makeStyles({
  avatar: (props: { isOutgoing: boolean }) => ({
    width: 32,
    height: 32,
    backgroundColor: props.isOutgoing ? '#E1BEE7' : '#90caf9',
    color: 'black',
    fontSize: '12px',
    flexShrink: 0,
  }),
  bubble: (props: { isOutgoing: boolean }) => ({
    padding: '0.5rem 0.75rem',
    borderRadius: props.isOutgoing ? '20px 20px 1px 20px' : '20px 20px 20px 1px',
    backgroundColor: props.isOutgoing ? green[500] : grey[200],
    color: props.isOutgoing ? 'white' : 'inherit',
  }),
});
